import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { Loader2, ShieldAlert } from "lucide-react";
import { formatMoney } from "@/lib/money";
import { RiskBadge } from "@/components/RiskBadge";

interface Row {
  id: string; gig_id: string; opener_role: string; reason: string; status: string;
  ai_risk: "low" | "medium" | "high" | null;
  worker_payout_cents: number | null; poster_refund_cents: number | null;
  created_at: string; resolved_at: string | null;
  gig?: { title: string; budget_cents: number; poster_id: string; worker_id: string | null };
}

const Disputes = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("gig_disputes")
        .select("*, gig:gigs(title, budget_cents, poster_id, worker_id)")
        .order("created_at", { ascending: false });
      setRows((data as Row[]) ?? []);
      setLoading(false);
    })();
  }, [user]);

  return (
    <MobileShell>
      <BrandHeader onBack={() => navigate(-1)} />
      <div className="px-4 pt-2 pb-6">
        <h1 className="font-display font-extrabold text-2xl flex items-center gap-2">
          <ShieldAlert className="h-5 w-5" /> Disputes
        </h1>
        <p className="text-xs text-muted-foreground">Open and resolved disputes you're part of.</p>
      </div>
      <div className="px-4 pb-24 space-y-3">
        {loading && <div className="grid place-items-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
        {!loading && rows.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border p-8 text-center space-y-3">
            <div className="mx-auto h-12 w-12 rounded-2xl bg-success/10 grid place-items-center text-success">
              <ShieldAlert className="h-6 w-6" />
            </div>
            <p className="text-sm text-muted-foreground">No disputes — clean record 🌴</p>
            <button
              onClick={() => navigate("/gigs")}
              className="text-sm font-semibold text-primary hover:underline"
            >
              Browse gigs →
            </button>
          </div>
        )}
        {rows.map((r) => (
          <button
            key={r.id}
            onClick={() => navigate(`/gigs/${r.gig_id}`)}
            className="w-full text-left rounded-2xl bg-card border border-border p-3 space-y-2"
          >
            <div className="flex items-start justify-between gap-2">
              <p className="font-display font-bold text-sm">{r.gig?.title ?? "Gig"}</p>
              <span className="text-[10px] uppercase font-semibold text-muted-foreground">{r.status.replace(/_/g, " ")}</span>
            </div>
            <p className="text-xs text-muted-foreground line-clamp-2">"{r.reason}"</p>
            <div className="flex items-center gap-2 flex-wrap">
              <RiskBadge risk={r.ai_risk} />
              <span className="text-[10px] text-muted-foreground">
                {formatMoney(r.gig?.budget_cents ?? 0)} · {new Date(r.created_at).toLocaleDateString()}
              </span>
            </div>
          </button>
        ))}
      </div>
    </MobileShell>
  );
};

export default Disputes;
